import React from 'react';
import { TrendingUp, Copyright } from 'lucide-react'; // Added Copyright for consistency
import { Link } from 'react-router-dom'; // For navigation links

const FooterLink: React.FC<{ href: string; children: React.ReactNode }> = ({ href, children }) => (
  <Link to={href} className="text-sm text-gray-400 hover:text-gray-200 transition-colors">
    {children}
  </Link>
);

const FooterSection: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div>
    <h3 className="text-md font-semibold text-gray-100 mb-3">{title}</h3>
    <ul className="space-y-2">
      {children}
    </ul>
  </div>
);

const AppFooter: React.FC = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-black text-gray-300 py-12 px-4 sm:px-6 lg:px-8">
      <div className="container mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
          {/* Keyword OS Section */}
          <div className="lg:col-span-2 pr-8">
            <div className="flex items-center mb-3">
              <TrendingUp className="h-7 w-7 mr-2 text-white" /> {/* Changed text-sky-400 to text-white */}
              <span className="text-xl font-bold text-white">Keyword OS</span>
            </div>
            <p className="text-sm text-gray-400 mb-4">
              Powerful keyword research tools.
            </p>
            <div className="flex items-center text-xs text-gray-500">
              <Copyright className="h-3 w-3 mr-1" /> 
              <span>{currentYear} Keyword OS. All rights reserved.</span>
            </div>
          </div>

          {/* Company Info Section */}
          <FooterSection title="Company Info">
            <li><FooterLink href="/about">About Us</FooterLink></li>
            <li><FooterLink href="/careers">Careers</FooterLink></li>
            <li><FooterLink href="/contact">Contact</FooterLink></li>
            <li><FooterLink href="/blog">Blog</FooterLink></li>
          </FooterSection>

          {/* Resources Section */}
          <FooterSection title="Resources">
            <li><FooterLink href="/help">Help Center</FooterLink></li>
            <li><FooterLink href="/case-studies">Case Studies</FooterLink></li>
          </FooterSection>

          {/* Legal Section */}
          <FooterSection title="Legal">
            <li><FooterLink href="/terms">Terms of Service</FooterLink></li>
            <li><FooterLink href="/privacy">Privacy Policy</FooterLink></li>
            <li><FooterLink href="/gdpr">GDPR Compliance</FooterLink></li>
            <li><FooterLink href="/cookies">Cookie Policy</FooterLink></li>
          </FooterSection>

          {/* Connect Section - This was not in the grid in the original footer, but for consistency with sections I am placing it here. Adjust if needed. */}
          {/* Or, if Connect should be separate: */}
          {/* 
          <div className="md:col-span-2 lg:col-span-1">  // Example of placing connect in its own column if desired
            <FooterSection title="Connect">
              <li><FooterLink href="https://twitter.com">Twitter</FooterLink></li>
              <li><FooterLink href="https://linkedin.com">LinkedIn</FooterLink></li>
              <li><FooterLink href="https://youtube.com">YouTube</FooterLink></li>
              <li><FooterLink href="https://instagram.com">Instagram</FooterLink></li>
            </FooterSection>
          </div>
          */}
        </div>
        
        {/* Centered Connect Section, if preferred like original image (which looked like separate columns) */}
        {/* To replicate the screenshot more closely, the connect links would be a fifth column. My grid setup handles this. */}
        {/* If a bottom line copyright is desired instead of in the Keyword OS block */}
        {/* 
        <div className="mt-8 pt-8 border-t border-slate-700 text-center text-xs text-gray-500">
           © {currentYear} Keyword OS. All rights reserved.
        </div>
        */}
      </div>
    </footer>
  );
};

export default AppFooter;
